import React from "react";

import { Indicator, Item, Root } from "@radix-ui/react-radio-group";
import twMerge from "@replo/design-system/utils/twMerge";
import { useControllableState } from "replo-utils/react/use-controllable-state";

import Description from "../label/Description";
import Label from "../label/Label";

type RadioButtonGroupProps = {
  size: "base" | "sm";
  disabled: boolean;
  options: RadioButtonProps[];
  onChange?: (e: string | undefined) => void;
  selectedValue?: string;
  defaultValueIndex?: number;
  layoutClassName?: string;
  variant?: "default" | "card";
  UNSAFE_className?: string;
};

type RadioButtonProps = {
  disabled?: boolean;
  value: string;
  label: string;
  description?: string;
  onChange?: (e: string | undefined) => void;
  selectedValue?: string;
};

const RadioButtonGroup: React.FC<RadioButtonGroupProps> = ({
  size,
  disabled = false,
  options = [],
  onChange,
  selectedValue,
  defaultValueIndex,
  layoutClassName,
  UNSAFE_className,
  variant = "default",
}) => {
  const defaultValue = defaultValueIndex
    ? options[defaultValueIndex]?.value
    : options[0]?.value;
  const [selectedRadioValue, setSelectedRadioValue] = useControllableState(
    selectedValue,
    defaultValue,
    onChange,
  );

  return (
    <Root
      className={twMerge(
        "flex flex-col gap-3",
        layoutClassName,
        UNSAFE_className,
      )}
      value={selectedRadioValue}
      onValueChange={setSelectedRadioValue}
      disabled={disabled}
    >
      {options.map((radioButtonOption) => {
        const optionDisabled = radioButtonOption.disabled ?? disabled;
        return (
          <label
            htmlFor={radioButtonOption.value}
            key={radioButtonOption.value}
            className={twMerge(
              "flex cursor-pointer items-start gap-2 font-normal",
              variant === "card" &&
                "border-0.5 border-borderhover:bg-sheet rounded-lg p-3",
              variant === "card" &&
                selectedRadioValue === radioButtonOption.value &&
                "border-primary bg-selectable-selected text-primary hover:bg-selectable-selected-hover",
              optionDisabled && "cursor-not-allowed opacity-50",
            )}
          >
            <Item
              className={twMerge(
                "border-0.5 border-border relative h-4 w-4 shrink-0 rounded-full bg-white",
                size === "sm" && "mt-[1px]",
                size === "base" && "mt-[3px]",
                optionDisabled && "cursor-not-allowed",
              )}
              value={radioButtonOption.value}
              id={radioButtonOption.value}
            >
              <Indicator className="after:bg-primary absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 after:block after:h-[9.14px] after:w-[9.14px] after:rounded-full after:content-['']" />
            </Item>
            {(radioButtonOption.label || radioButtonOption.description) && (
              <div className="min-w-0 flex-1 gap-1">
                <Label
                  as="div"
                  UNSAFE_className={twMerge(
                    optionDisabled && "cursor-not-allowed",
                  )}
                  label={radioButtonOption.label}
                  size={size}
                  breakWords
                  pointer
                />
                <Description
                  description={radioButtonOption.description}
                  size={size}
                  type="muted"
                  breakWords
                />
              </div>
            )}
          </label>
        );
      })}
    </Root>
  );
};

export { RadioButtonGroup };
